import { Add as AddIcon, KeyboardArrowDown as CaretIcon, Share } from '@mui/icons-material';
import { Alert, Box, CardContent, Chip, DialogActions, IconButton, List, ListItemButton, ListItemText, Stack } from '@mui/material';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks.js';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import MissionForm from '../components/MissionForm';
import { FullMission, useActiveMission, useMissionGroups, useMissions, useMissionsState, useSetActiveMission } from '../missions';
import CloseButton from './CloseButton';
import ListItemLink from './ListItemLink';
import { useMissionsRoute, useModule } from './ModuleContext';
import Timestamp from './Timestamp';
import Dialog from './dialog';

export default function MissionSelectorTitle() {
  const activeMission = useActiveMission();
  const { t } = useTranslation('einsaetze');

  if (!activeMission) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>{t('no-active-mission')}</span>
        <CreateMissionButton />
      </Box>
    );
  }

  return <MissionSelector mission={activeMission.mission} />;
}

function MissionSelector({ mission }: { mission: FullMission }) {
  const popupState = usePopupState({ variant: 'popover', popupId: 'mission-selector' });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} {...bindTrigger(popupState)}>
          <span>{[mission.keyword, mission.message].filter(Boolean).join(' ')}</span>
          <CaretIcon />
        </Box>
        <CreateMissionButton />
      </Box>
      <MissionSelectorDialog {...bindPopover(popupState)} />
    </>
  );
}

function CreateMissionButton() {
  const { state } = useMissionsState();
  const prefix = useMissionsRoute();
  const popupState = usePopupState({ variant: 'popover', popupId: 'create-mission' });
  const history = useHistory();
  const canCreate = useMissionGroups().some((g) => g.permissions.includes('create'));

  return state === 'ready' && canCreate ? (
    <>
      <IconButton color="inherit" {...bindTrigger(popupState)}>
        <AddIcon />
      </IconButton>
      <MissionForm open={popupState.isOpen} handleClose={popupState.close} handleSubmit={(m) => history.push(`${prefix}/${m.id}`)} />
    </>
  ) : null;
}

function MissionSelectorDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('einsaetze');

  return (
    <Dialog title={t('select.title')} open={open} onClose={onClose}>
      <MissionSelectorContent onClose={onClose} />
      <DialogActions>
        <CloseButton onClick={onClose}>{t('cancel')}</CloseButton>
      </DialogActions>
    </Dialog>
  );
}

function MissionSelectorContent({ onClose }: { onClose: () => void }) {
  const missions = useMissions();
  const { t } = useTranslation('einsaetze');

  if (!missions.length) {
    return (
      <CardContent>
        <Alert severity="info">{t('no-active-mission')}</Alert>
      </CardContent>
    );
  }

  return (
    <List>
      {missions.map((mission) => (
        <MissionItem key={mission.id} mission={mission} onClose={onClose} />
      ))}
    </List>
  );
}

function MissionItem({ mission, onClose }: { mission: FullMission; onClose: () => void }) {
  const activeMission = useActiveMission();
  const setActiveMission = useSetActiveMission();
  const selected = mission.id === activeMission?.mission?.id;

  const content = (
    <ListItemText
      primary={
        <Stack spacing={1} direction="row" alignItems="baseline">
          {mission.keyword && <Chip label={mission.keyword} color="secondary" />}
          <span>{mission.message}</span>
          {mission.shared === true && <Share sx={{ fontSize: '1em' }} />}
        </Stack>
      }
      secondary={
        <>
          {mission.address}
          {mission.address && <br />}
          <Timestamp target={mission.createdAt} />
        </>
      }
    />
  );

  const module = useModule();

  const selectMission = useCallback(() => {
    setActiveMission({ missionId: mission.id });
    onClose();
  }, [mission.id, onClose, setActiveMission]);

  return module?.supportsMissions ? (
    <ListItemLink selected={selected} divider to={`${module?.route}/${mission.id}`} onClick={onClose}>
      {content}
    </ListItemLink>
  ) : (
    <ListItemButton divider onClick={selectMission}>
      {content}
    </ListItemButton>
  );
}
